import { IconBrandGithub } from "@tabler/icons-react";
import { Alert, Button, Flex, Space, Spin, Tag, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import {
	useCompleteGitProviderConnection,
	useCreateGitProviderConnectionAuthUrl,
	useListGitProviderInstallations,
} from "@/common/artemis-operations/workspaces";
import type { ListGitProviderInstallationsMutation } from "@/common/artemis-sdk/graphql";
import { useNotify } from "@/common/use-notify";
import { CustomIcon } from "@/components/base";
import { CustomList } from "@/components/custom-list";
import { ResponsiveDialog } from "@/components/responsive-dialog";

export type GitHubInstallation =
	ListGitProviderInstallationsMutation["listGitProviderInstallations"][number];

/** Readable text for the reason codes the GitHub connect mutations return as their error message. */
const CONNECT_ERRORS: Record<string, string> = {
	installation_connected_to_another_organization:
		"This GitHub account is already connected to another StratoNext organization.",
	organization_already_connected:
		"Your organization is already connected to a different GitHub account. Disconnect it first.",
	state_already_used:
		"This GitHub link was already used. Start the connection again.",
	invalid_state: "The GitHub link expired. Start the connection again.",
	invalid_grant: "The GitHub sign-in expired. Start the connection again.",
	state_user_mismatch:
		"This GitHub link was started by someone else. Start the connection yourself.",
	oauth_failed: "GitHub sign-in failed. Start the connection again.",
	missing_oauth_code:
		"GitHub sign-in was not completed. Start the connection again.",
	app_not_enabled_for_organization:
		"Enable the GitHub app for your organization first.",
};

/** What GitHub sent back to the Admin → Apps page after sign-in or install. */
export type GitHubReturn = {
	state: string;
	code?: string;
	installationId?: string;
	setupAction?: string;
};

/** Carries a GitHubReturn from the window GitHub opened in back to the dialog that opened it. */
const RETURN_CHANNEL = "stratonext-github-connect";

/**
 * Hands a GitHubReturn to an open connect dialog in another window of this app. Resolves true once
 * that dialog acknowledges it (the caller can then close its own window), false after a short wait
 * when no dialog is listening — the caller then shows the picker itself.
 */
export function handOffGitHubReturn(ret: GitHubReturn): Promise<boolean> {
	return new Promise((resolve) => {
		const channel = new BroadcastChannel(RETURN_CHANNEL);
		const done = (handed: boolean) => {
			clearTimeout(timer);
			channel.close();
			resolve(handed);
		};
		const timer = setTimeout(() => done(false), 1000);
		channel.onmessage = (e) => {
			if (e.data?.ack === ret.state) done(true);
		};
		channel.postMessage({ type: "return", ret });
	});
}

/** Maps a connect mutation error to a message the user can act on. */
export function gitHubConnectErrorMessage(err: unknown): string {
	const reason = err instanceof Error ? err.message : "";
	return (
		CONNECT_ERRORS[reason] ?? "Failed to connect GitHub. Please try again."
	);
}

/** Where the connect dialog is; each step offers one primary action. */
type Step = "github" | "waiting" | "results" | "notInstalled";

/**
 * Connects the organization to GitHub (ADR-062: one installation per organization), one step at a time:
 * - **github:** sign in to GitHub in a new window to pick an account.
 * - **waiting:** until that window hands GitHub's answer back here (`handOffGitHubReturn`) and closes.
 * - **results / notInstalled:** pick the account, or install the App on GitHub when none is reachable.
 * Accounts connected to another organization are listed but can't be picked.
 */
export function GitHubInstallationPicker({
	open,
	githubReturn,
	onClose,
}: {
	open: boolean;
	/** A return GitHub sent to this same window (no dialog was open to hand it to). */
	githubReturn?: GitHubReturn;
	onClose: () => void;
}) {
	const notify = useNotify();
	const complete = useCompleteGitProviderConnection();
	const createAuthUrl = useCreateGitProviderConnectionAuthUrl();
	const listInstallations = useListGitProviderInstallations();
	const [step, setStep] = useState<Step>("github");
	const [installations, setInstallations] = useState<GitHubInstallation[]>([]);
	const [notice, setNotice] = useState<{
		type: "error" | "info";
		text: string;
	}>();

	function close() {
		setStep("github");
		setInstallations([]);
		setNotice(undefined);
		onClose();
	}

	function connected(accountLogin?: string | null) {
		notify.success(
			accountLogin ? `GitHub connected (${accountLogin})` : "GitHub connected",
		);
		close();
	}

	function failed(err: unknown) {
		setNotice({ type: "error", text: gitHubConnectErrorMessage(err) });
	}

	function connect(installation: GitHubInstallation) {
		if (!installation.grant) return;
		setNotice(undefined);
		complete.mutate(
			{
				installationId: installation.installationId,
				grant: installation.grant,
			},
			{
				onSuccess: (connection) => connected(connection.accountLogin),
				onError: failed,
			},
		);
	}

	// Lists the installations for a return. The state is single-use, so each is handled once (the
	// ref also absorbs StrictMode's double effect).
	const handledState = useRef<string | undefined>(undefined);
	const handleReturn = useRef((_ret: GitHubReturn) => {});
	handleReturn.current = (ret) => {
		if (handledState.current === ret.state) return;
		handledState.current = ret.state;
		setNotice(undefined);
		if (ret.setupAction === "request") {
			setStep("notInstalled");
			setNotice({
				type: "info",
				text: "Installation requested. A GitHub organization owner must approve it before it can be connected.",
			});
			return;
		}
		if (!ret.code) {
			setStep("github");
			failed(new Error("missing_oauth_code"));
			return;
		}
		setStep("waiting");
		listInstallations.mutate(
			{ state: ret.state, code: ret.code },
			{
				onSuccess: (list) => {
					setInstallations(list);
					const connectable = list.filter((i) => i.grant);
					// Nothing to choose between (or the user just installed one): connect straight away.
					const only =
						connectable.find((i) => i.installationId === ret.installationId) ??
						(connectable.length === 1 ? connectable[0] : undefined);
					setStep(connectable.length ? "results" : "notInstalled");
					if (only) connect(only);
				},
				onError: (err) => {
					setStep("github");
					failed(err);
				},
			},
		);
	};

	useEffect(() => {
		if (githubReturn) handleReturn.current(githubReturn);
	}, [githubReturn]);

	// While open, take returns handed over by the GitHub window.
	useEffect(() => {
		if (!open) return;
		const channel = new BroadcastChannel(RETURN_CHANNEL);
		channel.onmessage = (e) => {
			const ret: GitHubReturn | undefined =
				e.data?.type === "return" ? e.data.ret : undefined;
			if (!ret) return;
			channel.postMessage({ ack: ret.state });
			handleReturn.current(ret);
		};
		return () => channel.close();
	}, [open]);

	/** Opens GitHub sign-in (`url`) or App installation (`installUrl`) in a new window. */
	function openGitHub(target: "url" | "installUrl") {
		setNotice(undefined);
		createAuthUrl.mutate(undefined, {
			onSuccess: (data) => {
				window.open(data[target], "_blank", "noopener,noreferrer");
				setStep("waiting");
			},
			onError: failed,
		});
	}

	const account = (login: string, type: string) => (
		<Space vertical size={0}>
			<Typography.Text strong>{login}</Typography.Text>
			<Typography.Text>{type}</Typography.Text>
		</Space>
	);

	return (
		<ResponsiveDialog
			open={open}
			onOpenChange={(isOpen) => !isOpen && close()}
			title="Connect GitHub"
			footer={null}
		>
			<Space vertical size="large" style={{ width: "100%" }}>
				<Typography.Text>
					Connect your organization to one GitHub account. Workspaces where the
					GitHub app is enabled can then browse its repositories.
				</Typography.Text>

				{notice && <Alert type={notice.type} showIcon title={notice.text} />}

				{step === "github" && (
					<>
						<Typography.Text>
							Sign in to GitHub to pick the account to connect.
						</Typography.Text>
						<Button
							type="primary"
							loading={createAuthUrl.isPending}
							onClick={() => openGitHub("url")}
						>
							Continue on GitHub
						</Button>
					</>
				)}

				{step === "waiting" && (
					<Flex vertical align="center" gap="middle">
						<Spin />
						<Typography.Text>
							{listInstallations.isPending
								? "Loading your GitHub accounts…"
								: "Finish on GitHub in the other window…"}
						</Typography.Text>
						<Button onClick={() => setStep("github")}>Cancel</Button>
					</Flex>
				)}

				{(step === "results" || step === "notInstalled") && (
					<>
						{step === "notInstalled" && (
							<Typography.Text>
								The StratoNext app isn't installed on any GitHub account you can
								connect. Install it on the account whose repositories you want
								to use.
							</Typography.Text>
						)}
						{installations.length > 0 && (
							<CustomList
								title="Your GitHub accounts"
								data={installations}
								renderLeading={() => <CustomIcon icon={IconBrandGithub} />}
								renderItem={(i) =>
									account(i.accountLogin || i.installationId, i.accountType)
								}
								renderTrailing={(i) =>
									i.grant ? (
										<Button
											type="primary"
											loading={complete.isPending}
											onClick={() => connect(i)}
										>
											Connect
										</Button>
									) : (
										<Tag>Used by another organization</Tag>
									)
								}
							/>
						)}
						{step === "notInstalled" ? (
							<Button
								type="primary"
								icon={<CustomIcon icon={IconBrandGithub} />}
								loading={createAuthUrl.isPending}
								onClick={() => openGitHub("installUrl")}
							>
								Install the app on GitHub
							</Button>
						) : (
							<Button type="link" onClick={() => openGitHub("installUrl")}>
								Install the app on another GitHub account
							</Button>
						)}
					</>
				)}
			</Space>
		</ResponsiveDialog>
	);
}
